/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { isEmpty, replace } from "lodash"
import { Item, ModifierGroup } from "types/models/menu"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { extractMinMax, getDalErrMessage } from "utils/misc"
import { ResponseWithError, UpdateItemRequest } from "types/api"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { Dayjs } from "dayjs"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { availableStatuses, unifyHiddenStatus } from "utils/item_status"

export { availableStatuses, unifyHiddenStatus }

export enum availableStatusesUsage {
  TABLE_FILTER = "tableFilter",
  ITEM_SELECTOR = "itemSelector",
  MENU_TAG = "menuTag",
}

export const availableStatusesValue: {
  [availableStatus: number]: string
} = {
  [availableStatuses.AVAILABLE]: "AVAILABLE",
  [availableStatuses.UNAVAILABLE_TODAY]: "UNAVAILABLE_TODAY",
  [availableStatuses.UNAVAILABLE_PERMANENTLY]: "UNAVAILABLE_PERMANENTLY",
  [availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK]:
    "UNAVAILABLE_UNTIL_NEXT_RESTOCK",
  [availableStatuses.UNAVAILABLE_UNTIL_CUSTOM]: "UNAVAILABLE_UNTIL_CUSTOM",
  [availableStatuses.UNAVAILABLE_DUE_TO_ITEM_FILL_RATE]:
    "UNAVAILABLE_DUE_TO_ITEM_FILL_RATE",
  [availableStatuses.HIDDEN_UNTIL_REACTIVATED]: "HIDDEN_UNTIL_REACTIVATED",
  [availableStatuses.HIDDEN_DUE_TO_SELLING_TIME_EXPIRED]:
    "HIDDEN_DUE_TO_SELLING_TIME_EXPIRED",
}

const availableStatusesTranslationKeys: {
  [availableStatusesUsage: string]: { [availableStatus: number]: string }
} = {
  [availableStatusesUsage.ITEM_SELECTOR]: {
    [availableStatuses.AVAILABLE]: "menu.item.statuses.available",
    [availableStatuses.UNAVAILABLE_TODAY]:
      "menu.item.statuses.unavailableUntilTomorrow",
    [availableStatuses.UNAVAILABLE_PERMANENTLY]:
      "menu.item.statuses.unavailableIndefinitely",
    [availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK]:
      "menu.item.statuses.unavailableUntilRestock",
    [availableStatuses.UNAVAILABLE_UNTIL_CUSTOM]:
      "menu.item.filter.unavailableUntilCustom",
    [availableStatuses.UNAVAILABLE_DUE_TO_ITEM_FILL_RATE]:
      "menu.item.filter.unavailableDueToItemFillRate",
    [availableStatuses.HIDDEN_UNTIL_REACTIVATED]:
      "menu.item.statuses.hiddenUntilReactivated",
  },
  [availableStatusesUsage.TABLE_FILTER]: {
    [availableStatuses.AVAILABLE]: "menu.item.statuses.available",
    [availableStatuses.UNAVAILABLE_TODAY]:
      "menu.item.statuses.unavailableUntilTomorrow",
    [availableStatuses.UNAVAILABLE_PERMANENTLY]:
      "menu.item.statuses.unavailableIndefinitely",
    [availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK]:
      "menu.item.statuses.unavailableUntilRestock",
    [availableStatuses.UNAVAILABLE_UNTIL_CUSTOM]:
      "menu.item.filter.unavailableUntilCustom",
    [availableStatuses.UNAVAILABLE_DUE_TO_ITEM_FILL_RATE]:
      "menu.item.filter.unavailableDueToItemFillRate",
    [availableStatuses.HIDDEN_UNTIL_REACTIVATED]:
      "menu.item.statuses.hiddenUntilReactivated",
  },
  [availableStatusesUsage.MENU_TAG]: {
    [availableStatuses.UNAVAILABLE_TODAY]:
      "menu.item.statuses.unavailableUntilTomorrow",
    [availableStatuses.UNAVAILABLE_PERMANENTLY]:
      "menu.item.statuses.unavailableIndefinitely",
    [availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK]:
      "menu.item.statuses.unavailableUntilRestock",
    [availableStatuses.UNAVAILABLE_UNTIL_CUSTOM]:
      "menu.item.filter.unavailableUntilCustom",
    [availableStatuses.UNAVAILABLE_DUE_TO_ITEM_FILL_RATE]:
      "menu.item.statuses.unavailableIndefinitely",
    [availableStatuses.HIDDEN_UNTIL_REACTIVATED]:
      "menu.item.statuses.hiddenUntilReactivated",
  },
}

export const AvailableStatusesText = (usage: string) => {
  return useTranslationsMap(availableStatusesTranslationKeys[usage] ?? {})
}

export const MarkStatusesText = (pluralText: boolean) => {
  const singularStatusText = useTranslationsMap({
    [availableStatuses.AVAILABLE]:
      "menu.notifications.changeItemsAvailability.available.successMessage.singular",
    [availableStatuses.UNAVAILABLE_TODAY]:
      "menu.notifications.changeItemsAvailability.unavailableUntilTomorrow.successMessage.singular",
    [availableStatuses.UNAVAILABLE_PERMANENTLY]:
      "menu.notifications.changeItemsAvailability.unavailableIndefinitely.successMessage.singular",
    [availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK]:
      "menu.notifications.changeItemsAvailability.unavailableUntilNextRestock.successMessage.singular",
    [availableStatuses.HIDDEN_UNTIL_REACTIVATED]:
      "menu.notifications.changeItemsAvailability.hiddenUntilReactivated.successMessage.singular",
  })

  const pluralStatusText = useTranslationsMap({
    [availableStatuses.AVAILABLE]:
      "menu.notifications.changeItemsAvailability.available.successMessage.plural",
    [availableStatuses.UNAVAILABLE_TODAY]:
      "menu.notifications.changeItemsAvailability.unavailableUntilTomorrow.successMessage.plural",
    [availableStatuses.UNAVAILABLE_PERMANENTLY]:
      "menu.notifications.changeItemsAvailability.unavailableIndefinitely.successMessage.plural",
    [availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK]:
      "menu.notifications.changeItemsAvailability.unavailableUntilNextRestock.successMessage.plural",
    [availableStatuses.HIDDEN_UNTIL_REACTIVATED]:
      "menu.notifications.changeItemsAvailability.hiddenUntilReactivated.successMessage.plural",
  })

  if (pluralText) {
    return pluralStatusText
  }

  return singularStatusText
}

export const useConfirmDeleteItemOptions = () => ({
  title: useTranslation("menu.modals.deleteItem.title"),
  content: useTranslation("menu.modals.deleteItem.content"),
  okText: useTranslation("menu.buttons.delete"),
  cancelText: useTranslation("menu.buttons.cancel"),
  okButtonProps: {
    danger: true,
  },
})

export const availableStatusesStyle: {
  [usage: string]: { [availableStatus: number]: any }
} = {
  [availableStatusesUsage.ITEM_SELECTOR]: {
    [availableStatuses.UNAVAILABLE_TODAY]: {
      color: "#f55538",
    },
    [availableStatuses.UNAVAILABLE_PERMANENTLY]: {
      color: "#f55538",
    },
    [availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK]: {
      color: "#f55538",
    },
    [availableStatuses.UNAVAILABLE_UNTIL_CUSTOM]: {
      color: "#f55538",
    },
    [availableStatuses.UNAVAILABLE_DUE_TO_ITEM_FILL_RATE]: {
      color: "#f55538",
    },
  },
  [availableStatusesUsage.MENU_TAG]: {
    [availableStatuses.UNAVAILABLE_TODAY]: "yellow",

    [availableStatuses.UNAVAILABLE_PERMANENTLY]: "red",

    [availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK]: "yellow",

    [availableStatuses.UNAVAILABLE_UNTIL_CUSTOM]: "yellow",

    [availableStatuses.UNAVAILABLE_DUE_TO_ITEM_FILL_RATE]: "red",

    [availableStatuses.HIDDEN_UNTIL_REACTIVATED]: "grey",
  },
}

export const availableStatusesNonRecurring: number[] = [
  availableStatuses.AVAILABLE,
  availableStatuses.UNAVAILABLE_TODAY,
  availableStatuses.UNAVAILABLE_PERMANENTLY,
]

export const availableStatusesRecurring: number[] = [
  availableStatuses.AVAILABLE,
  availableStatuses.UNAVAILABLE_TODAY,
  availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK,
  availableStatuses.UNAVAILABLE_PERMANENTLY,
]

export const getAvailableStatusesNonRecurring = (): number[] => {
  const isHideItemEnabled = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.portalEnableHideItem,
  )
  return isHideItemEnabled
    ? [
        ...availableStatusesNonRecurring,
        availableStatuses.HIDDEN_UNTIL_REACTIVATED,
      ]
    : availableStatusesNonRecurring
}

export const getAvailableStatusesRecurring = (): number[] => {
  const isHideItemEnabled = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.portalEnableHideItem,
  )
  return isHideItemEnabled
    ? [
        ...availableStatusesRecurring,
        availableStatuses.HIDDEN_UNTIL_REACTIVATED,
      ]
    : availableStatusesRecurring
}

export const availableStatusesJustShow: number[] = [
  availableStatuses.UNAVAILABLE_UNTIL_CUSTOM,
  availableStatuses.UNAVAILABLE_DUE_TO_ITEM_FILL_RATE,
]

export const availableStatusesMixed: number[] = [
  availableStatuses.AVAILABLE,
  availableStatuses.UNAVAILABLE_PERMANENTLY,
]

export const getAvailableStatusesMixed = (): number[] => {
  const isHideItemEnabled = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.portalEnableHideItem,
  )
  return isHideItemEnabled
    ? [...availableStatusesMixed, availableStatuses.HIDDEN_UNTIL_REACTIVATED]
    : availableStatusesMixed
}

export const isRecurringItem: (item: Item) => boolean = item =>
  item.itemStock?.enableRestock === true

export const isSpecialItem: (item: Item) => boolean = item =>
  !isEmpty(item.specialItemType)

export const isItemAvailable: (item: Item) => boolean = item =>
  item.availableStatus === 1

export const itemWeightUnits = ["ml", "L", "g", "kg", "per pack"]

export const generateModifierGroupRule = (
  modifierGroup: ModifierGroup,
  textMap: {
    pickExactly: string
    pickXtoY: string
    modifierGroupPickUpTo: string
  },
) => {
  let modifierGroupRule: string = ""
  const { selectionRangeMax, selectionRangeMin } = modifierGroup
  const requireExactly = selectionRangeMax === selectionRangeMin
  const requireInBetween =
    selectionRangeMin > 0 && selectionRangeMin !== selectionRangeMax
  const optionalWithMax = selectionRangeMin === 0 && selectionRangeMax > 0
  if (requireExactly) {
    // Require exactly
    modifierGroupRule = textMap.pickExactly
      .replace(/<x>/i, `${modifierGroup?.modifiers?.length}`)
      .replace(/<y>/i, `${selectionRangeMin}`)
  } else if (requireInBetween) {
    // Require inBetween
    modifierGroupRule = textMap.pickXtoY
      .replace(/<x>/, `${modifierGroup?.modifiers?.length}`)
      .replace(/<y>/, `${selectionRangeMin}`)
      .replace(/<z>/, `${selectionRangeMax}`)
  } else if (optionalWithMax) {
    // Optional with maximum selection
    modifierGroupRule = textMap.modifierGroupPickUpTo
      .replace(/<x>/i, `${modifierGroup?.modifiers?.length}`)
      .replace(/<y>/i, `${selectionRangeMax}`)
  }
  return modifierGroupRule
}

export function getLinkedModifierGroups(
  modifierGroups: ModifierGroup[],
  itemID?: string,
  arrangedLinkedModifierGroupIDs?: string[] | null,
): ModifierGroup[] {
  if (!itemID) {
    return []
  }
  return (
    modifierGroups
      ?.filter(mg => mg?.relatedItemIDs.includes(itemID))
      ?.sort((a, b) =>
        arrangedLinkedModifierGroupIDs
          ? arrangedLinkedModifierGroupIDs.indexOf(a.modifierGroupID) -
            arrangedLinkedModifierGroupIDs.indexOf(b.modifierGroupID)
          : 0,
      ) ?? []
  )
}

export function itemToUpdateItemRequest(item: Item): UpdateItemRequest {
  return {
    categoryID: item?.categoryID ?? "",
    item,
    itemID: item.itemID ?? "",
    itemIDs: [item.itemID ?? ""],
    availableStatus: item?.availableStatus ?? 0,
  }
}

export const itemErrorTargets = {
  ExceedChangeLimit: "ExceedChangeLimit",
  ExceedMaxItemLimit: "ExceedMaxItemLimit",
  ExceedMaxItemLimitForNotArchived: "ExceedMaxItemLimitForNotArchived",
  ItemPromo: "ItemPromo",
  ItemPromoAttributeLimit: "ItemPromoAttributeLimit",
  ItemPromoDeleteLimit: "ItemPromoDeleteLimit",
  ItemIsArchived: "ItemIsArchived",
  InvalidPrice: "InvalidPrice",
  ExceedConstraint: "ExceedConstraint",
  ExceedItemDescriptionLengthLimit: "ExceedItemDescriptionLengthLimit",
  ItemWeightValueInvalid: "ItemWeightValueInvalid",
  ItemWeightCountInvalid: "ItemWeightCountInvalid",
  InternalFEMenuGroupItemOngoingPromotion:
    "InternalFEMenuGroupItemOngoingPromotion", // internal FE key
}

export const itemErrorTargetTranslationKeys = {
  [itemErrorTargets.ExceedMaxItemLimit]: "menu.errors.item.exceedMaxItemLimit",
  [itemErrorTargets.ExceedMaxItemLimitForNotArchived]:
    "menu.errors.item.exceedMaxItemLimit",
  [itemErrorTargets.ItemPromo]: "menu.errors.item.itemPromo",
  [itemErrorTargets.ItemPromoAttributeLimit]:
    "menu.errors.item.itemPromoAttributeLimit",
  [itemErrorTargets.ItemPromoDeleteLimit]:
    "menu.errors.item.itemPromoDeleteLimit",
  [itemErrorTargets.ItemIsArchived]: "menu.errors.item.archived",
  [itemErrorTargets.InvalidPrice]: "menu.errors.price.priceInvalid",
  [itemErrorTargets.ExceedConstraint]:
    "menu.errors.price.priceExceedConstraint",
  [itemErrorTargets.ExceedItemDescriptionLengthLimit]:
    "menu.errors.characterLimit",
  [itemErrorTargets.ItemWeightValueInvalid]:
    "catalogue.errors.sizePerUnitMinMax",
  [itemErrorTargets.ItemWeightCountInvalid]: "catalogue.errors.noOfItemsMinMax",
  [itemErrorTargets.InternalFEMenuGroupItemOngoingPromotion]:
    "menu.errors.item.ongoingPromotion",
}

enum itemErrorMessageSubstring {
  ONGOING_PROMOTION = "item has ongoing promotion",
}

export const useItemWithBackTimeMessageText = (time: Dayjs) => {
  const day = time.format("ddd") || ""
  const textMap = useTranslationsMap({
    Sun: "common.days.sun",
    Mon: "common.days.mon",
    Tue: "common.days.tue",
    Wed: "common.days.wed",
    Thu: "common.days.thu",
    Fri: "common.days.fri",
    Sat: "common.days.sat",
  })
  const dateText = `${textMap[day]}, ${time.format("DD MMM")}`
  return useTranslation("menu.item.statuses.unavailableUntilCustom").replace(
    "<daydate.short.noyear>",
    dateText,
  )
}

export const useItemErrorMessageText = () => {
  return useTranslationsMap(itemErrorTargetTranslationKeys)
}

const checkErrorMessage = (msg: string, textMap: { [key: string]: string }) => {
  if (msg.includes(itemErrorMessageSubstring.ONGOING_PROMOTION)) {
    return textMap[itemErrorTargets.InternalFEMenuGroupItemOngoingPromotion]
  }
  return ""
}

export const useItemErrorResponseToMessage = () => {
  const textMap = useItemErrorMessageText()
  const {
    menuGroupDetail: { menuGroupID },
    isMenuGroup,
  } = useSelector((state: AppState) => state.menu)
  return (response: ResponseWithError): string | true => {
    const { message = "", target = "" } = response ?? {}

    switch (target) {
      case itemErrorTargets.ExceedMaxItemLimit:
      case itemErrorTargets.ExceedMaxItemLimitForNotArchived: {
        return replace(textMap[target], "$count", message)
      }
      case itemErrorTargets.ExceedItemDescriptionLengthLimit:
        return replace(textMap[target], "$limit", message)
      case itemErrorTargets.ItemWeightCountInvalid:
      case itemErrorTargets.ItemWeightValueInvalid: {
        // (min:1,max:2)
        const { min = "?", max = "?" } = extractMinMax(message)
        return textMap[target].replace(/<min>/, min).replace(/<max>/, max)
      }
      default:
        if (isMenuGroup && menuGroupID) {
          const dalMsg = getDalErrMessage(message)
          if (dalMsg) {
            return dalMsg
          }

          const errMsg = checkErrorMessage(message, textMap)
          if (errMsg) {
            return errMsg
          }
        }

        // default error message
        return textMap[target] ?? true
    }
  }
}
